import { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Home } from './features/home/Home';
import { useStandaloneMode } from './hooks/useStandaloneMode';
import { apiClient } from './lib/apiClient';
import { FEATURE_FLAGS } from './lib/featureFlags';
import { useAuthStore } from './lib/stores/authStore';

/**
 * Корневой роут `/` — Главная (Экран 2.0, Block 0). App.tsx — тонкая
 * обёртка для глобальных эффектов (auth-redirect, mount-fetch profile recovery).
 *
 * Block 0 Phase 2 (ADR-0012):
 *
 * 1. **Auth-redirect (Stage 1 gate):** если `FEATURE_FLAGS.ONBOARDING_ENABLED`
 *    и пользователь не прошёл Stage 1 (`!isOnboarded()`), редиректим на
 *    `/onboarding/lang-city`. Проверка `!startsWith('/onboarding')` нужна,
 *    чтобы не зацикливать redirect (и для будущих под-роутов /onboarding/*).
 *
 * 2. **Mount-fetch profile recovery (Phase 4 заменяет ADR-0012 nuance #2):**
 *    на mount проверяем, не остался ли `jwt` в localStorage без in-memory
 *    `profile` + `subscription` после refresh. Это естественное состояние
 *    после любого refresh, потому что P-009 запрещает persist'ить ПДн и
 *    subscription state. Phase 2 nuance #2 был palliative (logout()); Phase 4
 *    делает корректное profile recovery через `apiClient.me.get()` →
 *    `setSubscription(...)`. На failure (401 / network / parse) — graceful
 *    `logout()` (старый Phase 2 fallback). См. ADR-0014 §«Решение» Q6.
 *
 *    `subscription === null` в условии — guard от бесконечного refetch loop.
 *    Profile остаётся null (Phase 4 сознательное решение — `/api/me` НЕ
 *    возвращает name/phone/specialization, это P-007 → региональный Postgres
 *    + отдельный `/api/profile` resource в Phase 9+, см. B-068). Без guard
 *    условие `jwt && !profile` оставалось бы true после успешного fetch'а →
 *    бесконечный цикл. С guard: subscription становится non-null после
 *    setSubscription → re-render не запускает второй fetch.
 *
 * Block 0 Phase 3 (ADR-0013) — PWA install gate перед onboarding:
 *
 * 3. **Install-redirect (`/` → `/install` → `/onboarding/lang-city`):** если
 *    `FEATURE_FLAGS.PWA_INSTALL_ENABLED`, пользователь не onboarded и устройство
 *    не в standalone-режиме — редиректим на `/install` ДО онбординга. Это
 *    PRD §2.1 happy path. Выходные точки `/install`:
 *      - native install (`appinstalled`) → Container навигирует на
 *        `/onboarding/lang-city`;
 *      - skip / iOS «Понятно» → `/onboarding/lang-city`;
 *      - standalone detected внутри Container → `<Navigate to="/onboarding/lang-city" replace />`.
 *    `standalone` snapshot в App.tsx — fast path: пользователь уже установил
 *    PWA и зашёл с иконки → install screen бесполезен, идём прямо в
 *    онбординг. Onboarded user не редиректится никогда (даже на новом
 *    устройстве) — install-первый-раз остаётся за свежим юзером.
 *
 * Порядок проверок: `/install` приоритетнее `/onboarding/lang-city`. Без него
 * чейн пошёл бы `/` → `/onboarding/lang-city`, минуя install screen.
 *
 * **Порядок Phase 1/2/3/4 redirect chain (важно для понимания):**
 *   - Phase 4 mount-fetch (useEffect) — async, не блокирует render.
 *   - Phase 2 redirect (return Navigate) — sync, выполняется ПЕРВЫМ при render.
 *   - Phase 3 install-first — sub-ветка Phase 2 (внутри isOnboarded() === false).
 *   Mount-fetch и redirect-цепочка независимы: пока fetch в полёте, redirect
 *   уже отработал на основе текущего store-state. После setSubscription —
 *   re-render с обновлённым state, redirect снова отрабатывает.
 */
export function App() {
  const isOnboarded = useAuthStore((s) => s.isOnboarded);
  const jwt = useAuthStore((s) => s.jwt);
  const profile = useAuthStore((s) => s.profile);
  const subscription = useAuthStore((s) => s.subscription);
  const logout = useAuthStore((s) => s.logout);
  const setSubscription = useAuthStore((s) => s.setSubscription);
  const standalone = useStandaloneMode();
  const location = useLocation();

  useEffect(() => {
    // Phase 4 mount-fetch (ADR-0014 Q6). Условие охватывает кейс post-refresh,
    // когда P-009-persisted jwt оживает без in-memory profile/subscription.
    // `subscription === null` — loop guard: после успешного fetch subscription
    // становится non-null → useEffect не перезапускается, потому что условие
    // false (а значит cancelled-cleanup из предыдущего вызова уже выполнился).
    if (jwt === null || profile !== null || subscription !== null) return;

    let cancelled = false;
    apiClient.me
      .get()
      .then((me) => {
        if (cancelled) return;
        // Profile НЕ touched — `/api/me` не содержит name/phone/specialization
        // (P-007 региональный Postgres). Реальный profile fetch — Phase 9+
        // через отдельный `/api/profile` (B-068). Phase 4 ограничивается
        // subscription state (что и нужно для paywall logic в Step 4).
        setSubscription({
          status: me.subscriptionStatus,
          trialStartedAt: me.trialStartedAt,
          expiresAt: me.expiresAt,
        });
      })
      .catch(() => {
        // 401 (expired/invalid JWT) / NETWORK_ERROR / PARSE_ERROR (backend drift) →
        // graceful degradation на guest mode. logout() очищает jwt+subscription,
        // сохраняет lang/city/region (UX continuity per ADR-0012 §8). Не
        // различаем тип ошибки специально: для пользователя любое из этих
        // состояний означает «не удалось восстановить сессию».
        if (cancelled) return;
        logout();
      });

    return () => {
      cancelled = true;
    };
  }, [jwt, profile, subscription, logout, setSubscription]);

  // Defensive: `App` сейчас mount'ится только на `/` (см. router.tsx), поэтому
  // `pathname.startsWith('/onboarding'|'/install')` всегда false. Условие
  // оставлено на случай будущей миграции на wildcard layout route — без него
  // инициация App-обёртки на /onboarding/* или /install привела бы к
  // зацикленному redirect.
  const onAuthRoute =
    location.pathname.startsWith('/onboarding') || location.pathname.startsWith('/install');

  if (FEATURE_FLAGS.ONBOARDING_ENABLED && !isOnboarded() && !onAuthRoute) {
    if (FEATURE_FLAGS.PWA_INSTALL_ENABLED && !standalone) {
      return <Navigate to="/install" replace />;
    }
    return <Navigate to="/onboarding/lang-city" replace />;
  }

  return <Home />;
}
